/**
 * Single source of truth for FAQs — consumed by both the on-page accordion
 * (FaqAccordion) and the FAQPage JSON-LD, so the rendered Q&A and the
 * structured data can never drift apart. Written for AI summarization (GEO):
 * each answer is self-contained and directly answers the question.
 */
import type { FaqItem } from '@/lib/jsonld';

export interface Faq extends FaqItem {
  id: string;
}

export const FAQS: Faq[] = [
  {
    id: 'what-is-typing-test',
    question: 'What is a typing test?',
    answer:
      'A typing test measures how fast and accurately you can type. You are shown a passage of words and type them as quickly as you can; the test then reports your speed in words per minute (WPM), your accuracy as a percentage, and often your characters per minute (CPM). TypeSwift is a free online typing test that works in 8 languages with no login required.',
  },
  {
    id: 'what-is-wpm',
    question: 'What is WPM (words per minute)?',
    answer:
      'WPM stands for words per minute — the standard measure of typing speed. Because real words vary in length, typing tests define one "word" as 5 characters including spaces. WPM is therefore calculated as (characters typed ÷ 5) ÷ minutes elapsed. The average adult types around 40 WPM, while professional typists reach 65–100 WPM.',
  },
  {
    id: 'what-is-cpm',
    question: 'What is CPM (characters per minute)?',
    answer:
      'CPM stands for characters per minute — the total number of characters you type in one minute. Unlike WPM, it does not divide by a fixed word length, so it is useful for comparing typing speed across languages and scripts. As a rough guide, CPM is about five times your WPM.',
  },
  {
    id: 'what-is-accuracy',
    question: 'What is typing accuracy?',
    answer:
      'Accuracy is the percentage of your keystrokes that were correct. If you type 200 characters and 6 are wrong, your accuracy is 97%. High accuracy matters because every error costs time to find, delete, and retype. Most experts recommend keeping accuracy above 97% before pushing for higher speed.',
  },
  {
    id: 'how-speed-calculated',
    question: 'How is typing speed calculated?',
    answer:
      'Typing speed is calculated by dividing the number of standardized words (total characters ÷ 5) by the time taken in minutes. For example, typing 300 characters in 1 minute equals 60 WPM. TypeSwift measures your speed in real time and shows your final WPM, CPM, and accuracy when the timer ends.',
  },
  {
    id: 'gross-vs-net-wpm',
    question: 'What is the difference between Gross WPM and Net WPM?',
    answer:
      'Gross WPM counts all the words you typed without subtracting mistakes: (all characters ÷ 5) ÷ minutes. Net WPM subtracts your errors to reflect usable speed: Net WPM = Gross WPM − (uncorrected errors ÷ minutes). Net WPM is the more accurate measure of real-world typing performance because it rewards accuracy, not just speed.',
  },
  {
    id: 'how-typing-tests-work',
    question: 'How do typing tests work?',
    answer:
      'A typing test shows you a set of words and starts a timer on your first keystroke. As you type, it compares each character against the expected text, marking correct and incorrect entries. When the time runs out (commonly 30, 60, 120, or 300 seconds), it calculates your WPM, CPM, and accuracy from what you typed. TypeSwift highlights the current word and tints your input green or red so you get instant feedback.',
  },
  {
    id: 'what-is-good-wpm',
    question: 'What is a good typing speed?',
    answer:
      'A good typing speed for most people is 60–70 WPM with high accuracy. Beginners typically start around 20–30 WPM, the average adult types about 40 WPM, and professionals reach 80–100 WPM. Anything above 100 WPM puts you in the top few percent of typists worldwide.',
  },
  {
    id: 'how-to-improve',
    question: 'How can I improve my typing speed?',
    answer:
      'Improve your typing speed by learning proper touch typing (all ten fingers, no looking at the keyboard), prioritizing accuracy over raw speed, and practicing 15 minutes a day. Use the home row as your anchor, keep good posture, and take a short typing test daily to track progress. Consistency matters far more than long, occasional sessions.',
  },
  {
    id: 'who-is-this-for',
    question: 'Who is TypeSwift for?',
    answer:
      'TypeSwift is for anyone who wants to type faster: students, professionals, writers, developers, data-entry operators, and candidates preparing for government and competitive typing exams. Because it is free, multilingual, and requires no account, it suits both casual self-improvement and serious exam preparation.',
  },
  {
    id: 'why-students-practice',
    question: 'Why should students practice typing?',
    answer:
      'Students who type faster and more accurately complete assignments, take notes, and finish online exams more efficiently. Typing is now a core digital-literacy skill, and many competitive and government exams include a typing test. Building the skill early gives students an advantage in both academics and future careers.',
  },
  {
    id: 'government-exams',
    question: 'Which government exams require typing tests?',
    answer:
      'Many clerical, data-entry, and stenographer government posts include a typing test — for example SSC CHSL and SSC CGL (Data Entry Operator), railway (RRB) clerical posts, court and police data-entry roles, and various state-level exams. Typical requirements are around 30–35 WPM in English and 25–30 WPM in Hindi or regional languages. Always check your official exam notification for the exact requirement.',
  },
  {
    id: 'who-benefits',
    question: 'Who benefits the most from typing practice?',
    answer:
      'Anyone who works with a keyboard benefits, but the biggest gains go to data-entry operators, office workers, programmers, content writers, customer-support agents, and exam aspirants. Faster, more accurate typing saves hours of work over time and reduces fatigue and errors.',
  },
  {
    id: 'is-it-free',
    question: 'Is TypeSwift free to use?',
    answer:
      'Yes. TypeSwift is completely free, with no account, login, sign-up, or payment required. You can take unlimited typing tests in any supported language at no cost.',
  },
  {
    id: 'how-many-languages',
    question: 'How many languages does TypeSwift support?',
    answer:
      'TypeSwift currently supports typing tests in 8 languages: English, Spanish, French, German, Hindi, Portuguese, Italian, and Russian. Each language uses a frequency-based word list so the test reflects real, commonly used words. More languages are planned.',
  },
  {
    id: 'best-typing-test',
    question: 'What is the best typing test?',
    answer:
      'The best typing test is one that is free, gives instant and accurate WPM, CPM, and accuracy results, works without a login, and lets you practice in your own language. TypeSwift is a strong choice because it is completely free, requires no sign-up, supports 8 languages including English, Hindi, and Russian, offers 30-second to 5-minute durations, randomizes words so scores stay honest, and saves your personal best in your browser so you can track improvement over time.',
  },
  {
    id: 'best-free-typing-test-online',
    question: 'What is the best free typing test online?',
    answer:
      'A good free online typing test should measure WPM, CPM, and accuracy in real time, need no download or account, and use realistic word lists. TypeSwift is a free online typing test that meets all of these: it runs entirely in your browser, shows live speed and accuracy feedback, supports multiple languages and test durations, and keeps your data private by storing scores locally.',
  },
  {
    id: 'multilingual',
    question: 'Can I take a typing test in different languages?',
    answer:
      'Yes. You can switch the test language at any time from the language selector. TypeSwift loads a language-specific word list so you practice with words that are actually common in that language, which makes the speed measurement more realistic.',
  },
  {
    id: 'test-durations',
    question: 'What test durations are available?',
    answer:
      'TypeSwift offers 30-second, 1-minute, 2-minute, and 5-minute tests. Shorter tests are great for quick speed checks, while longer tests give a more reliable picture of your sustained typing speed and stamina.',
  },
  {
    id: 'accuracy-vs-speed',
    question: 'Does accuracy matter more than speed?',
    answer:
      'In most professional settings, accuracy matters more. A typist at 70 WPM with 99% accuracy produces more usable text than one at 85 WPM with 92% accuracy, because errors take time to fix. Build accuracy to 97%+ first, then work on speed.',
  },
  {
    id: 'mobile',
    question: 'Does TypeSwift work on mobile?',
    answer:
      'TypeSwift is fully responsive and works on phones and tablets. For the most accurate WPM measurement, a physical keyboard is recommended, since on-screen keyboards add latency. On mobile, the keyboard appears automatically when you tap the input field.',
  },
  {
    id: 'data-privacy',
    question: 'Where are my scores stored? Is my data private?',
    answer:
      'All your scores are stored only in your browser using localStorage. TypeSwift has no backend, database, or user accounts, so your results never leave your device. Clearing your browser data or using a private window resets your personal best.',
  },
  {
    id: 'typing-certificate',
    question: 'Can I get a typing certificate?',
    answer:
      'A typing certificate documents your measured WPM and accuracy and can be useful for job and exam applications. TypeSwift lets you measure a verifiable speed score you can use to demonstrate your typing ability. Check our certificate page for the latest details.',
  },
  {
    id: 'typing-lessons',
    question: 'Does TypeSwift offer typing lessons?',
    answer:
      'Yes. Beyond timed tests, TypeSwift provides typing practice and lessons designed to build touch-typing technique, finger placement, and accuracy step by step, so you improve in a structured way rather than just testing yourself.',
  },
  {
    id: 'why-randomized',
    question: 'Why are the words randomized?',
    answer:
      'Words are randomized on every test so you cannot memorize the passage and inflate your score. This keeps each result honest and representative of your true typing speed on unfamiliar text.',
  },
  {
    id: 'touch-typing',
    question: 'What is touch typing?',
    answer:
      'Touch typing is typing without looking at the keyboard, using all ten fingers with each finger assigned to specific keys. It relies on muscle memory anchored at the home row (ASDF for the left hand, JKL; for the right). Touch typing is the foundation of fast, accurate typing.',
  },
  {
    id: 'how-often-practice',
    question: 'How often should I practice typing?',
    answer:
      'Practice typing for about 15 minutes every day. Short, consistent daily sessions build muscle memory far more effectively than occasional long sessions. Daily practice with a 1-minute test plus tracking your personal best is enough for most people to see steady improvement within a few weeks.',
  },
  {
    id: 'no-login',
    question: 'Do I need to create an account?',
    answer:
      'No account or login is needed. Open TypeSwift and start typing immediately. Your personal best is saved locally in your browser so you can track progress without signing up.',
  },
];
